import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './PlanSettingsModal.css';

const useLockBodyScroll = () => {
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, []);
};

const PlanSettingsModal = ({ plan, onClose, onVisibilityChange, onDeleteRequest }) => {
  const [visibility, setVisibility]               = useState(plan?.visibility ?? false);
  const [pendingVisibility, setPendingVisibility] = useState(plan?.visibility ?? false);
  const [saving, setSaving]                       = useState(false);
  const [saved, setSaved]                         = useState(false);

  useLockBodyScroll();

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    await onVisibilityChange(pendingVisibility);
    setVisibility(pendingVisibility);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return createPortal(
    <div className="psm-overlay" onClick={onClose}>
      <div className="psm-panel" onClick={(e) => e.stopPropagation()}>

        {/* HEADER */}
        <div className="psm-header">
          <span className="psm-title">Vision Settings</span>
          <button className="psm-close" onClick={onClose} aria-label="Close settings">
            <svg viewBox="0 0 20 20" fill="none">
              <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* VISION NAME */}
        {plan?.name && (
          <div className="psm-vision-name">"{plan.name}"</div>
        )}

        <div className="psm-divider" />

        {/* VISIBILITY SECTION */}
        <div className="psm-section">
          <div className="psm-section-label">Visibility</div>
          <p className="psm-section-desc">
            Control who can see this vision and its nodes.
          </p>

          <div className="psm-visibility-options">
            <button
              className={`psm-vis-option ${pendingVisibility === false ? 'active' : ''}`}
              onClick={() => setPendingVisibility(false)}
            >
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <rect x="4" y="9" width="12" height="9" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M7 9V6a3 3 0 016 0v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <div>
                <span className="psm-vis-label">Private</span>
                <span className="psm-vis-desc">Only you can see this</span>
              </div>
            </button>

            <button
              className={`psm-vis-option ${pendingVisibility === true ? 'active' : ''}`}
              onClick={() => setPendingVisibility(true)}
            >
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M10 3c-2 2-3 4.5-3 7s1 5 3 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M10 3c2 2 3 4.5 3 7s-1 5-3 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M3 10h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <div>
                <span className="psm-vis-label">Public</span>
                <span className="psm-vis-desc">Anyone with the link can view</span>
              </div>
            </button>
          </div>

          {/* Save row — only shows when something changed */}
          {pendingVisibility !== visibility && (
            <div className="psm-vis-save-row">
              <span className="psm-vis-change-hint">
                Changing to <strong>{pendingVisibility ? 'Public' : 'Private'}</strong>
              </span>
              <button
                className="psm-vis-save-btn"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          )}

          {saved && (
            <p className="psm-saved-confirm">
              <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M3 8l3.5 3.5L13 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Saved
            </p>
          )}
        </div>

        <div className="psm-divider" />

        {/* DANGER ZONE */}
        <div className="psm-section psm-danger-section">
          <div className="psm-section-label psm-danger-label">Danger Zone</div>
          <div className="psm-danger-row">
            <div>
              <p className="psm-danger-title">Delete this Vision</p>
              <p className="psm-danger-desc">Permanently removes this vision and all its nodes. Cannot be undone.</p>
            </div>
            <button className="psm-delete-btn" onClick={onDeleteRequest}>
              Delete
            </button>
          </div>
        </div>

      </div>
    </div>,
    document.body
  );
};

export default PlanSettingsModal;